import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  TrendingUp, 
  Target, 
  Lightbulb, 
  ArrowRight,
  Star,
  Trophy,
  Zap,
  BookOpen,
  Users,
  Briefcase
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface DimensionScore {
  name: string;
  score: number;
  maxScore: number;
  color: string;
}

interface CareerStage {
  name: string;
  range: number[];
  color: string;
  description: string;
}

interface ResultsDashboardProps {
  totalScore: number;
  dimensionScores: DimensionScore[];
  careerStage: CareerStage;
  answers: Record<string, number>;
}

const DIMENSION_DETAILS = {
  technical: {
    name: 'Technical Capability',
    description: 'Domain expertise and execution effectiveness',
    color: 'text-accent-blue',
    bg: 'bg-accent-blue/10'
  },
  industry: {
    name: 'Industry Expertise', 
    description: 'Market knowledge and sector impact',
    color: 'text-accent-green',
    bg: 'bg-accent-green/10'
  },
  leadership: {
    name: 'Leadership Impact',
    description: 'Influence and organizational direction',
    color: 'text-accent-orange', 
    bg: 'bg-accent-orange/10'
  },
  communication: {
    name: 'Communication Effectiveness',
    description: 'Stakeholder management and relationship building',
    color: 'text-accent-pink',
    bg: 'bg-accent-pink/10'
  },
  growth: {
    name: 'Growth Trajectory',
    description: 'Learning velocity and advancement potential',
    color: 'text-primary',
    bg: 'bg-primary/10'
  }
};

const CAREER_STAGES = [
  { name: "Explorer", range: [0, 35], color: "text-career-explorer" },
  { name: "Achiever", range: [36, 65], color: "text-career-achiever" },
  { name: "Specialist", range: [66, 89], color: "text-career-specialist" },
  { name: "Authority", range: [90, 109], color: "text-career-authority" },
  { name: "Guru", range: [110, 120], color: "text-career-guru" }
];

export const ResultsDashboard = ({ 
  totalScore, 
  dimensionScores, 
  careerStage,
  answers 
}: ResultsDashboardProps) => {
  const getScoreColor = (score: number) => {
    if (score >= 20) return "text-accent-green";
    if (score >= 15) return "text-accent-yellow";
    if (score >= 10) return "text-accent-orange";
    return "text-accent-pink";
  };

  const getNextLevel = () => {
    const currentIndex = CAREER_STAGES.findIndex(stage => stage.name === careerStage.name);
    return currentIndex < CAREER_STAGES.length - 1 ? CAREER_STAGES[currentIndex + 1] : null;
  };

  const nextLevel = getNextLevel();
  const pointsToNext = nextLevel ? nextLevel.range[0] - totalScore : 0;

  const getStrengths = () => {
    return dimensionScores
      .filter(dim => (dim.score / dim.maxScore) >= 0.8)
      .slice(0, 3);
  };

  const getGrowthAreas = () => {
    return dimensionScores
      .filter(dim => (dim.score / dim.maxScore) < 0.6)
      .slice(0, 3);
  };

  const strengths = getStrengths();
  const growthAreas = getGrowthAreas();

  const recommendations = [
    {
      icon: BookOpen,
      title: "Skill Development",
      description: "Focus on your growth areas with targeted learning",
      action: "View Learning Plan"
    },
    {
      icon: Users,
      title: "Network Building", 
      description: "Connect with industry leaders and mentors",
      action: "Explore Networking"
    },
    {
      icon: Briefcase,
      title: "Project Portfolio",
      description: "Document your achievements and impact",
      action: "Build Portfolio"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-purple-950/10 to-background">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto space-y-8">
          
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Trophy className="w-8 h-8 text-primary" />
              <h1 className="text-3xl md:text-4xl font-bold text-text-primary">
                Your Loaded Score™ Results
              </h1>
            </div>
            <p className="text-text-secondary text-lg max-w-2xl mx-auto">
              Comprehensive analysis of your professional capabilities and personalized growth recommendations
            </p>
          </div>

          {/* Score Summary */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2 p-8 border-0 shadow-xl bg-gradient-to-br from-card-bg to-card-hover">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-text-primary">Overall Score</h2>
                    <p className="text-text-secondary">Your comprehensive capability metric</p>
                  </div>
                  <div className="text-right">
                    <div className={cn("text-5xl font-bold", getScoreColor(totalScore))}>
                      {totalScore}
                    </div>
                    <div className="text-text-muted">out of 120</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Career Stage: {careerStage.name}</span>
                    <Badge variant="secondary" className={careerStage.color}>
                      {careerStage.range[0]} - {careerStage.range[1]}
                    </Badge>
                  </div>
                  <Progress value={(totalScore / 120) * 100} className="h-3" />
                </div>
              </div>
            </Card>

            <Card className="p-6 border-0 shadow-xl bg-gradient-to-br from-primary/10 to-secondary/10">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-6 h-6 text-primary" />
                  <h3 className="text-lg font-semibold">Next Level</h3>
                </div>
                {nextLevel ? (
                  <>
                    <div>
                      <div className="text-2xl font-bold text-text-primary">{nextLevel.name}</div>
                      <div className="text-text-secondary">
                        {pointsToNext} points away
                      </div>
                    </div>
                    <Progress value={((totalScore - careerStage.range[0]) / (nextLevel.range[0] - careerStage.range[0])) * 100} className="h-2" />
                  </>
                ) : (
                  <div className="text-center py-4">
                    <Star className="w-8 h-8 mx-auto text-accent-yellow mb-2" />
                    <div className="text-lg font-semibold text-text-primary">You've reached the top!</div>
                    <div className="text-text-secondary text-sm">Industry Guru level achieved</div>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Dimension Breakdown */}
          <Card className="p-8 border-0 shadow-xl bg-gradient-to-br from-card-bg to-card-hover">
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-text-primary">Capability Dimensions</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {dimensionScores.map((dimension, index) => {
                  const percentage = (dimension.score / dimension.maxScore) * 100;
                  return (
                    <div key={index} className="p-6 rounded-xl bg-gradient-to-br from-white/5 to-white/10">
                      <div className="space-y-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className={cn("font-semibold", dimension.color)}>
                              {dimension.name}
                            </h3>
                            <p className="text-text-secondary text-sm">
                              Score: {dimension.score} / {dimension.maxScore}
                            </p>
                          </div>
                          <Badge variant="secondary" className={cn("font-bold", getScoreColor(dimension.score))}>
                            {Math.round(percentage)}%
                          </Badge>
                        </div>
                        <Progress value={percentage} className="h-2" />
                        <div className="text-xs text-text-muted">
                          {percentage >= 80 ? 'Strength' : 
                           percentage >= 60 ? 'Developing' : 'Growth Area'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>

          {/* Strengths & Growth Areas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6 border-0 shadow-xl bg-gradient-to-br from-accent-green/10 to-accent-cyan/5">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Star className="w-6 h-6 text-accent-green" />
                  <h3 className="text-xl font-bold text-text-primary">Your Strengths</h3>
                </div>
                <div className="space-y-3">
                  {strengths.length > 0 ? strengths.map((strength, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                      <Zap className="w-4 h-4 text-accent-green" />
                      <span className="text-text-primary font-medium">{strength.name}</span>
                    </div>
                  )) : (
                    <p className="text-text-secondary text-sm">Complete more sections to identify strengths</p>
                  )}
                </div>
              </div>
            </Card>

            <Card className="p-6 border-0 shadow-xl bg-gradient-to-br from-accent-orange/10 to-accent-yellow/5">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Target className="w-6 h-6 text-accent-orange" />
                  <h3 className="text-xl font-bold text-text-primary">Growth Opportunities</h3>
                </div>
                <div className="space-y-3">
                  {growthAreas.length > 0 ? growthAreas.map((area, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                      <Lightbulb className="w-4 h-4 text-accent-orange" />
                      <span className="text-text-primary font-medium">{area.name}</span>
                    </div>
                  )) : (
                    <p className="text-text-secondary text-sm">Great work! No major growth areas identified.</p>
                  )}
                </div>
              </div>
            </Card>
          </div>

          {/* Recommendations */}
          <Card className="p-8 border-0 shadow-xl bg-gradient-to-br from-card-bg to-card-hover">
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-text-primary">Recommended Next Steps</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {recommendations.map((rec, index) => (
                  <div key={index} className="p-6 rounded-xl bg-gradient-to-br from-primary/5 to-secondary/5 hover:from-primary/10 hover:to-secondary/10 transition-all duration-200 cursor-pointer group">
                    <div className="space-y-4">
                      <rec.icon className="w-8 h-8 text-primary group-hover:scale-110 transition-transform" />
                      <div>
                        <h3 className="font-semibold text-text-primary mb-2">{rec.title}</h3>
                        <p className="text-text-secondary text-sm mb-4">{rec.description}</p>
                        <Button variant="ghost" size="sm" className="text-primary p-0 h-auto hover:bg-transparent">
                          {rec.action}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-gradient-primary hover:opacity-90 text-lg px-8 py-6">
              <Trophy className="w-5 h-5 mr-2" />
              Share Your Score
            </Button>
            <Button variant="outline" size="lg" className="text-lg px-8 py-6">
              <BookOpen className="w-5 h-5 mr-2" />
              Get Personalized Plan
            </Button>
          </div>

        </div>
      </div>
    </div>
  );
};